import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Radio, Users,
} from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { FeatureStatusBanner } from "@/components/sartn/FeatureStatusBanner";
import { LoadingSkeleton } from "@/components/sartn/LoadingSkeleton";
import { TelegramConnectionGuide } from "@/components/sartn/TelegramConnectionGuide";
import { useChannelStore } from "@/stores/channelStore";
import { cn } from "@/lib/cn";
import type { ChannelConfig, ChannelType } from "@/stores/channelStore";

const SPRING = { type: "spring" as const, stiffness: 300, damping: 30 };

const LIST_CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const LIST_ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: SPRING },
};

const CHANNEL_TYPE_COLORS: Record<ChannelType, string> = {
  crypto: "bg-amber-50 text-amber-700",
  ecommerce: "bg-violet-50 text-violet-700",
  news: "bg-blue-50 text-blue-700",
  education: "bg-emerald-50 text-emerald-700",
  entertainment: "bg-pink-50 text-pink-700",
  community: "bg-cyan-50 text-cyan-700",
  other: "bg-gray-50 text-gray-700",
};

const CHANNEL_TYPE_LABELS: Record<ChannelType, string> = {
  crypto: "Crypto",
  ecommerce: "E-Commerce",
  news: "News",
  education: "Education",
  entertainment: "Entertainment",
  community: "Community",
  other: "Other",
};

function navigateToChannel(channelId: string) {
  window.location.hash = `/channels/${channelId}`;
}

export default function ChannelsPage() {
  const { channels, isLoading, fetchChannels } = useChannelStore();

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  const countBadge = channels.length > 0 && (
    <span
      className="flex h-6 min-w-6 items-center justify-center rounded-full
                 bg-[var(--color-primary)] px-1.5 text-xs font-semibold text-white"
    >
      {channels.length}
    </span>
  );

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title="Channels" onBack={() => window.history.back()} rightAction={countBadge} />

      <main className="flex-1 px-4 pb-20 pt-2">
        <FeatureStatusBanner
          status="limited"
          title="Live channels, limited summaries / ቀጥታ ቻናሎች፣ ውስን ማጠቃለያ"
          description="Channel management and scheduling use the live API; list-level post summaries are still being aggregated. / የቻናል አስተዳደርና መርሐግብር ቀጥታ API ይጠቀማሉ፤ የፖስት ማጠቃለያ ገና በመዘጋጀት ላይ ነው።"
        />

        {isLoading && (
          <LoadingSkeleton variant="list" count={3} className="mt-2" />
        )}

        {!isLoading && channels.length === 0 && (
          <TelegramConnectionGuide kind="channel" />
        )}

        {!isLoading && channels.length > 0 && (
          <motion.div
            variants={LIST_CONTAINER_VARIANTS}
            initial="hidden"
            animate="visible"
            className="mt-2 flex flex-col gap-3"
          >
            {channels.map((channel) => (
              <motion.div key={channel.id} variants={LIST_ITEM_VARIANTS}>
                <ChannelCard
                  channel={channel}
                  onClick={() => navigateToChannel(channel.id)}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </main>
    </div>
  );
}

function ChannelCard({
  channel,
  onClick,
}: {
  channel: ChannelConfig;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.98 }}
      className={cn(
        "flex w-full flex-col gap-3 rounded-[var(--radius-lg)] p-4",
        "bg-[var(--color-surface)] text-left shadow-sm transition-colors",
        "active:bg-[var(--color-border-subtle)]",
      )}
      aria-label={`Manage ${channel.title} channel`}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-full",
            "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
          )}
        >
          <Radio className="h-5 w-5" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="truncate text-sm font-semibold text-[var(--color-text)]">
            {channel.title}
          </span>
          <div className="flex items-center gap-2">
            <Users className="h-3 w-3 text-[var(--color-text-muted)]" />
            <span className="text-xs text-[var(--color-text-muted)]">
              {channel.subscriberCount.toLocaleString()} subscribers
            </span>
            <ChannelTypeBadge type={channel.channelType} />
          </div>
        </div>

        <ActiveDot isActive={channel.isActive} />
      </div>

      <div className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
        <span>{channel.stats.totalPosts} posts</span>
        <span aria-hidden="true">&middot;</span>
        <span>{channel.stats.postsThisWeek} this week</span>
        <span aria-hidden="true">&middot;</span>
        <span>{channel.scheduledPosts} scheduled</span>
      </div>
    </motion.button>
  );
}

function ChannelTypeBadge({ type }: { type: ChannelType }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-[var(--radius-full)] px-2 py-0.5",
        "text-[10px] font-medium",
        CHANNEL_TYPE_COLORS[type],
      )}
    >
      {CHANNEL_TYPE_LABELS[type]}
    </span>
  );
}

function ActiveDot({ isActive }: { isActive: boolean }) {
  return (
    <div
      className={cn(
        "h-2.5 w-2.5 shrink-0 rounded-full",
        isActive ? "bg-emerald-600" : "bg-red-600",
      )}
      aria-label={isActive ? "Active" : "Inactive"}
      role="status"
    />
  );
}
